import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  type Node,
  type Edge,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import type { WorkflowDiff, DiffState } from '@/schemas/workflow'
import { normalizeGraphToReactFlow } from '@/components/workflows/flow/normalize'
import { nodeTypes } from '@/components/workflows/flow/nodes'
import { edgeTypes } from '@/components/workflows/flow/edges'

/**
 * Color-style map. Uses the Phase-03 `--diff-*` CSS vars from `index.css`
 * so the same palette is reusable outside this component.
 */
const palette: Record<
  DiffState,
  { border: string; background: string; stroke: string }
> = {
  added: {
    border: '2px solid hsl(var(--diff-added))',
    background: 'hsl(var(--diff-added-soft))',
    stroke: 'hsl(var(--diff-added))',
  },
  removed: {
    border: '2px dashed hsl(var(--diff-removed))',
    background: 'hsl(var(--diff-removed-soft))',
    stroke: 'hsl(var(--diff-removed))',
  },
  modified: {
    border: '2px solid hsl(var(--diff-modified))',
    background: 'hsl(var(--diff-modified-soft))',
    stroke: 'hsl(var(--diff-modified))',
  },
  unchanged: {
    border: '2px solid transparent',
    background: 'transparent',
    stroke: 'hsl(var(--muted-foreground) / 0.4)',
  },
}

interface DiffCanvasProps {
  diff: WorkflowDiff
}

/**
 * Read-only React Flow canvas that overlays diff color semantics on top
 * of the newer snapshot, and renders removed nodes as ghost placeholders
 * at their old positions so users see what disappeared.
 *
 * Non-color affordance: each colored node also gets `data.diffChip` set
 * to `+` / `-` / `✎` so colorblind users have a secondary signal. Custom
 * node components can opt into rendering the chip.
 */
export function DiffCanvas({ diff }: DiffCanvasProps) {
  const { nodes: styledNodes, edges: styledEdges } = useMemo(() => {
    const chipFor: Record<DiffState, string> = {
      added: '+',
      removed: '-',
      modified: '✎',
      unchanged: '',
    }

    // Present nodes: render the NEW snapshot when present, coloring by state.
    const presentNodes = diff.nodes
      .filter((nd) => nd.state !== 'removed' && nd.new)
      .map((nd) => {
        const src = nd.new as Record<string, unknown>
        return {
          ...src,
          data: {
            ...(src.data as Record<string, unknown> | undefined),
            ...src,
            diffState: nd.state,
            diffChip: chipFor[nd.state],
          },
          style: {
            border: palette[nd.state].border,
            background: palette[nd.state].background,
          },
        } as Record<string, unknown>
      })

    // Ghost nodes: removed-in-newer → render old snapshot dashed + 50% opacity.
    const ghostNodes = diff.nodes
      .filter((nd) => nd.state === 'removed' && nd.old)
      .map((nd) => {
        const src = nd.old as Record<string, unknown>
        return {
          ...src,
          data: {
            ...(src.data as Record<string, unknown> | undefined),
            ...src,
            diffState: 'removed' as DiffState,
            diffChip: chipFor.removed,
          },
          style: {
            border: palette.removed.border,
            background: palette.removed.background,
            opacity: 0.5,
          },
        } as Record<string, unknown>
      })

    const presentEdges = diff.edges
      .filter((ed) => ed.state !== 'removed' && ed.new)
      .map((ed) => {
        const src = ed.new as Record<string, unknown>
        return {
          ...src,
          data: {
            ...(src.data as Record<string, unknown> | undefined),
            diffState: ed.state,
          },
          style: { stroke: palette[ed.state].stroke },
        } as Record<string, unknown>
      })

    const ghostEdges = diff.edges
      .filter((ed) => ed.state === 'removed' && ed.old)
      .map((ed) => {
        const src = ed.old as Record<string, unknown>
        return {
          ...src,
          data: {
            ...(src.data as Record<string, unknown> | undefined),
            diffState: 'removed' as DiffState,
          },
          style: { stroke: palette.removed.stroke, opacity: 0.5 },
        } as Record<string, unknown>
      })

    const { nodes, edges } = normalizeGraphToReactFlow(
      [...presentNodes, ...ghostNodes],
      [...presentEdges, ...ghostEdges]
    )

    // Re-attach diff style metadata lost to normalization (position + data).
    const nodesWithStyle = nodes.map((n, i) => {
      const src = [...presentNodes, ...ghostNodes][i]
      return {
        ...n,
        style: (src?.style as React.CSSProperties) ?? undefined,
        data: {
          ...n.data,
          diffState: (src?.data as { diffState?: DiffState } | undefined)
            ?.diffState,
          diffChip: (src?.data as { diffChip?: string } | undefined)?.diffChip,
        },
      }
    })
    const edgesWithStyle = edges.map((e, i) => {
      const src = [...presentEdges, ...ghostEdges][i]
      return {
        ...e,
        style: (src?.style as React.CSSProperties) ?? undefined,
        data: {
          ...(e.data ?? {}),
          diffState: (src?.data as { diffState?: DiffState } | undefined)
            ?.diffState,
        },
      }
    })
    return { nodes: nodesWithStyle, edges: edgesWithStyle }
  }, [diff])

  return (
    <ReactFlowProvider>
      <div className="relative h-full w-full" data-testid="diff-canvas">
        <ReactFlow
          nodes={styledNodes as Node[]}
          edges={styledEdges as Edge[]}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag
          zoomOnScroll
          fitView
          proOptions={{ hideAttribution: true }}
          className="bg-background"
        >
          <Background color="rgba(99, 102, 241, 0.15)" gap={20} size={1.2} />
          <Controls showInteractive={false} className="archon-flow-controls" />
          <MiniMap className="archon-flow-minimap" />
        </ReactFlow>
        <FloatingLegend />
      </div>
    </ReactFlowProvider>
  )
}

function FloatingLegend() {
  const { t } = useTranslation('workflows')
  const items: Array<{ key: string; color: string; dashed?: boolean }> = [
    { key: 'added', color: 'hsl(var(--diff-added))' },
    { key: 'removed', color: 'hsl(var(--diff-removed))', dashed: true },
    { key: 'modified', color: 'hsl(var(--diff-modified))' },
    { key: 'unchanged', color: 'hsl(var(--muted-foreground) / 0.5)' },
  ]
  return (
    <aside
      className="glass-strong absolute top-4 right-4 z-10 rounded-lg px-4 py-3 shadow-xl"
      aria-label="Legenda do diff"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 mb-2">
        Legenda
      </div>
      <ul className="flex flex-col gap-1.5">
        {items.map((it) => (
          <li
            key={it.key}
            className="flex items-center gap-2 text-[11px] text-foreground/90"
          >
            <span
              aria-hidden
              className="inline-block w-4 h-[2px] rounded-sm"
              style={{
                backgroundColor: it.dashed ? 'transparent' : it.color,
                border: it.dashed ? `1px dashed ${it.color}` : undefined,
              }}
            />
            {t(`lifecycle.diff.legend.${it.key}`)}
          </li>
        ))}
      </ul>
    </aside>
  )
}
